import { Link } from "react-router-dom";
import { readStoredText, writeStoredText } from "../../lib/safe-storage";
import { useEffect, useState } from "react";
import { buildSteps, currentPosition, type GuideState, type GuideStep } from "./steps";

const DISMISSED_KEY = "guide.dismissed";
const COLLAPSED_KEY = "guide.collapsed";

/**
 * The five-step routine, shown where someone is working.
 *
 * Dismissible and remembered, because this exists for the coworker meeting the
 * tool for the first time and must not nag the analyst who runs it daily.
 * Preference reads go through `safe-storage`, which cannot throw: a browser
 * denying storage shows the flow every time rather than breaking the page.
 *
 * `variant="bar"` is the collapsed-by-default form for the results screen,
 * where the steps are a reminder beside the work rather than the work itself.
 */
export function GuideStepper({ state, variant = "full" }: { state: GuideState; variant?: "full" | "bar" }) {
  const [dismissed, setDismissed] = useState(() => readStoredText(DISMISSED_KEY) === "1");
  const [collapsed, setCollapsed] = useState(
    () => readStoredText(COLLAPSED_KEY) === "1" || variant === "bar"
  );

  useEffect(() => {
    writeStoredText(COLLAPSED_KEY, collapsed ? "1" : "0");
  }, [collapsed]);

  if (dismissed) return null;

  const steps = buildSteps(state);
  const current = steps.find((step) => step.status === "current") ?? null;
  const position = currentPosition(steps);

  const dismiss = () => {
    writeStoredText(DISMISSED_KEY, "1");
    setDismissed(true);
  };

  return (
    <section
      className="rounded-sm border border-slate-300 bg-white"
      data-testid="guide-stepper"
      aria-label="Analysis routine"
    >
      <div className="flex flex-wrap items-center gap-2 px-3 py-2">
        <button
          type="button"
          className="text-sm font-medium text-sky-700 underline"
          data-testid="guide-toggle"
          aria-expanded={!collapsed}
          onClick={() => setCollapsed((value) => !value)}
        >
          {collapsed ? "Show the steps" : "Hide the steps"}
        </button>
        <span className="text-sm text-slate-700" data-testid="guide-summary">
          {current === null || position === null
            ? `All ${steps.length} steps done.`
            : `Step ${position} of ${steps.length}: ${current.label}. ${current.detail}`}
        </span>
        <button
          type="button"
          className="ml-auto text-xs text-slate-600 underline"
          data-testid="guide-dismiss"
          onClick={dismiss}
        >
          Don't show this again
        </button>
      </div>

      {collapsed ? null : (
        <ol className="grid gap-px border-t bg-slate-200 md:grid-cols-5" data-testid="guide-steps">
          {steps.map((step, index) => (
            <li key={step.id} className="bg-white p-3" data-testid={`guide-step-${step.id}`}>
              <p className="flex items-center gap-1.5 text-sm font-medium">
                <StatusMark status={step.status} />
                <span>
                  {index + 1}. {step.label}
                </span>
              </p>
              <p className="mt-1 text-xs text-slate-600">{step.detail}</p>
              {step.href === null ? null : (
                <Link className="mt-1 inline-block text-xs text-sky-700 underline" to={step.href}>
                  Go to {step.label.toLowerCase()}
                </Link>
              )}
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}

/**
 * The status, in text as well as colour.
 *
 * The word is what the accessible name carries; the tint only reinforces it.
 * A flow that encoded "done" as green alone would be unreadable to anyone who
 * cannot distinguish it, and the axe gate is not the reason to avoid that.
 */
function StatusMark({ status }: { status: GuideStep["status"] }) {
  const label: Record<GuideStep["status"], string> = {
    done: "Done",
    current: "Now",
    pending: "Later",
    "not-needed": "Not needed"
  };
  const tone: Record<GuideStep["status"], string> = {
    done: "border-green-300 bg-green-50 text-green-800",
    current: "border-sky-300 bg-sky-50 text-sky-800",
    pending: "border-slate-300 bg-slate-100 text-slate-600",
    "not-needed": "border-slate-300 bg-slate-100 text-slate-600"
  };
  return <span className={`rounded-sm border px-1.5 py-0.5 text-[11px] font-medium ${tone[status]}`}>{label[status]}</span>;
}
